import { FileUploadSettingsComponent } from './file-upload-settings.component';
import type { DashboardTileFileUpload } from '../../../dvm-layout-builder/dvm-layout-builder.types';
import { roundGeometry } from '../../utils/design-geometry.utils';
/**
 * The right-hand Design tab: every property of the current selection.
 *
 * Sections appear only when they apply to the selected node's type — a TEXT
 * node has typography but no widget binding, a FRAME has auto-layout, and so
 * on. With nothing selected it falls back to the canvas/artboard settings, so
 * the pane is never empty.
 *
 * Edits are pushed straight to the store with a stable label per control, so
 * dragging a number input coalesces into a single undo entry.
 */

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LucideAngularModule } from 'lucide-angular';

import { DesignDocumentStore } from '../../state/design-document.store';
import type {
  AutoLayout,
  DesignNode,
  Fill,
  Shadow,
  SlideOverlapMode,
  ValueColorRange,
  Stroke,
  TextContent,
  WidgetContent,
} from '../../design-builder.types';
import { WIDGET_LABELS } from '../../renderers/design-widget-host.component';
import { iconDataFor } from '../icon-library/design-icon-catalogue';
import { ICON_ACTION_OPTIONS } from '../../utils/design-icon-actions.utils';

import { withSampleData } from '../../renderers/design-sample-data';
import { COLUMN_CELL_CONFIGS_KEY, NODE_LEVEL_FORMAT_KEYS, resolveWidgetData } from '../../renderers/design-widget-data.utils';
import type { NestedTableLevelOverride } from '../../../components/nested-table/nested-table.types';
import { NestedTableColumnsComponent } from './nested-table-columns.component';
import { TableMatrixColumnsComponent, tableColumnValuesOf, tableMatrixColumnsOf, type TableColumnCellConfigs } from './table-matrix-columns.component';
import { ZoneSliderSettingsComponent } from './zone-slider-settings.component';
import { ButtonSettingsComponent } from './button-settings.component';
import { boundTextComponents, chartTargets, tileDataset, tileFromNode, widgetNodes } from '../../data/design-tiles.utils';
import { popupScreenIdsOf, screensOf } from '../../utils/design-publish.utils';
import { DesignCanvasStore } from '../../state/design-canvas.store';
import type { CellButtonOptions } from './cell-buttons.component';
import { buttonConfigOf, opensPopupScreen } from '../../../../global-components/dvm-action-button/dvm-action-button.types';
import { createScreenFrame, flattenNodes, locateNode } from '../../utils/design-node.utils';
import { nodeNumber, type BoundValueReader, type FormulaValueReader } from '../../utils/design-slide.utils';
import { numericValue } from '../../utils/design-value-color.utils';
import { isInputWidget } from '../../utils/design-input-value.utils';
import { VALUE_FORMULA_KEY } from '../../utils/design-formula.utils';
import { valueSourceOptionsOf } from '../../utils/design-save-request.utils';
import { APPROVAL_TABLE_TYPES, WIDGET_PALETTE_META } from '../../design-builder.constants';
import { readJson } from '../../../shared/self-service-storage.utils';
import { zoneSliderBindingOf } from '../../renderers/zone-slider-binding.utils';
import { DesignDataService, formatBoundValue } from '../../services/design-data.service';
import { DesignRuntimeService, columnLimitId } from '../../data/design-runtime.service';
import { autoRefreshSecondsOf, MIN_AUTO_REFRESH_SECONDS } from '../../utils/design-refresh.utils';
import type { NumberScale, TextDataBinding, TextFormula } from '../../design-builder.types';
import {
  APPEARANCE_OPTIONS,
  NO_FORMAT_LAYOUTS,
  ORIENTATION_OPTIONS,
  appearanceSpecFor,
  currentOrientation,
  defaultAppearance,
  currentVariant,
  variantByValue,
  variantOptionsFor,
} from '../../renderers/design-appearance.spec';
import {
  DEFAULT_FORMAT_LEVELS,
  applicableFormatControls,
  formatControlVisible,
  formatGroupOrderOf,
  groupFormatControls,
} from '../../../dvm-layout-builder/dvm-builder-config.utils';
import type {
  BuilderLayoutConfig,
  FormatControlGroup,
  LabelValueOption,
} from '../../../shared/self-service-config.types';

const SAVED_DASHBOARDS_STORAGE_KEY = 'dvm.selfService.savedDashboards';

/** Used until the builder config resolves; mirrors its first few entries. */
const FALLBACK_FONT_FAMILIES: LabelValueOption[] = [
  { label: 'Default', value: 'Inter, Roboto, sans-serif' },
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Segoe UI', value: "'Segoe UI', Tahoma, sans-serif" },
  { label: 'Roboto', value: "Roboto, 'Segoe UI', sans-serif" },
  { label: 'Georgia', value: "Georgia, 'Times New Roman', serif" },
  { label: 'Courier New', value: "'Courier New', Courier, monospace" },
  { label: 'JetBrains Mono', value: "'JetBrains Mono', monospace" },
];
import { DesignControlRendererComponent } from './design-control-renderer.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../../shared/ss-select-tooltip.directive';
import { CanvasFiltersComponent } from './canvas-filters.component';

@Component({
  selector: 'app-design-inspector',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatTooltipModule,
    LucideAngularModule,
    DesignControlRendererComponent,
    NgSelectModule,
    SsSelectTooltipDirective,
    NestedTableColumnsComponent,
    TableMatrixColumnsComponent,
    ZoneSliderSettingsComponent,
    ButtonSettingsComponent,
    FileUploadSettingsComponent,
    CanvasFiltersComponent,
  ],
  templateUrl: './design-inspector.component.html',
  styleUrl: './design-inspector.component.scss',
})
export class DesignInspectorComponent {
  readonly minAutoRefreshSeconds = MIN_AUTO_REFRESH_SECONDS;
  private readonly doc = inject(DesignDocumentStore);
  /** The canvas viewport — absent outside the builder. */
  private readonly canvasView = inject(DesignCanvasStore, { optional: true });

  /** The merged DVM builder config — the source of every design-tier control. */
  readonly layoutConfig = input<BuilderLayoutConfig | null>(null);
  // Dataset / field / aggregation inputs and the bind + configure outputs used
  // to live here. They are the DATA TIER and were removed on purpose: the
  // design pass comes first, and dead scaffolding for a later phase is worse
  // than none. Git history has them when the mapping layer starts.

  readonly node = this.doc.selectedNode;
  readonly selectionCount = computed(() => this.doc.selectedIds().size);
  readonly canvas = this.doc.canvas;

  readonly fontWeights = [300, 400, 500, 600, 700, 800];

  /**
   * Font choices for the Typography section.
   *
   * Reuses `options.fontFamilies` from the builder config — the same list the
   * chart Format controls offer — so a text node and a chart title can never
   * drift onto different font sets. Falls back to a small stack if the config
   * has not resolved yet.
   */
  readonly fontFamilies = computed<LabelValueOption[]>(() => {
    const configured = (this.layoutConfig()?.options as Record<string, unknown> | undefined)?.[
      'fontFamilies'
    ];
    if (Array.isArray(configured) && configured.length) {
      return configured as LabelValueOption[];
    }
    return FALLBACK_FONT_FAMILIES;
  });

  /** Whether a stored font stack is one the dropdown offers. */
  isKnownFont(font: string): boolean {
    return this.fontFamilies().some((option) => option.value === font);
  }
  readonly alignments: Array<TextContent['align']> = ['left', 'center', 'right', 'justify'];

  // ── Section visibility ─────────────────────────────────────────────────────
  readonly showCanvas = computed(() => !this.node());
  readonly showLayout = computed(() => this.node()?.type === 'FRAME');

  // ── Frame = screen ─────────────────────────────────────────────────────────

  /** The page itself: its box is derived, so it is reported, not edited. */
  readonly isScreen = computed(() => {
    const node = this.node();
    return !!node && this.doc.isScreen(node.id);
  });
  readonly showTypography = computed(() => this.node()?.type === 'TEXT');
  readonly showImage = computed(() => this.node()?.type === 'IMAGE');
  readonly showIcon = computed(() => this.node()?.type === 'ICON');
  readonly showWidget = computed(() => this.node()?.type === 'WIDGET');
  /** A LINE is drawn as its stroke, so it has no fill section. */
  readonly showFill = computed(() => {
    const type = this.node()?.type;
    return !!type && type !== 'LINE' && type !== 'GROUP';
  });

  readonly widgetLabel = computed(() => {
    const layoutType = this.node()?.widget?.layoutType ?? '';
    return WIDGET_LABELS[layoutType] ?? layoutType;
  });

  readonly fill = computed<Fill>(() => this.node()?.style?.fill ?? { kind: 'none' });
  readonly stroke = computed<Stroke | null>(() => this.node()?.style?.stroke ?? null);
  readonly shadow = computed<Shadow | null>(() => this.node()?.style?.shadow ?? null);
  readonly layout = computed<AutoLayout | null>(() => this.node()?.layout ?? null);
  readonly text = computed<TextContent | null>(() => this.node()?.text ?? null);

  /** Radius shown as a single number; the four-corner form stays in the JSON. */
  readonly radius = computed(() => {
    const radius = this.node()?.style?.radius;
    return Array.isArray(radius) ? radius[0] : (radius ?? 0);
  });

  readonly opacityPercent = computed(() => Math.round((this.node()?.opacity ?? 1) * 100));

  // ── Move with value ────────────────────────────────────────────────────────

  private readonly readBound: BoundValueReader = (binding) => {
    const state = this.dataService.value(binding);
    return state.status === 'ready' ? state.value : null;
  };

  private readonly readFormula: FormulaValueReader = (node) => {
    const result = node.formula?.expression ? this.dataService.formula(node.formula, node.id) : null;
    return result?.status === 'ready' ? result.value : null;
  };

  /** The number the selected node moves by, read from its own text. */
  readonly slideValue = computed(() => {
    const node = this.node();
    return node?.slide ? nodeNumber(node, this.readBound, this.readFormula) : null;
  });

  /** "Also move": siblings that travel with it (any layer except its stops). */
  readonly slideFollowerCandidates = computed(() => {
    const node = this.node();
    if (!node?.slide) return [];
    const stopIds = new Set(node.slide.stopIds);
    const followerIds = new Set(node.slide.followerIds ?? []);
    return (locateNode(this.doc.nodes(), node.id)?.siblings ?? [])
      .filter((sibling) => sibling.id !== node.id && !stopIds.has(sibling.id))
      .map((sibling) => ({ id: sibling.id, name: sibling.name, checked: followerIds.has(sibling.id) }));
  });

  /** Dropdown items — computed, so ng-select keeps the same arrays between checks. */
  readonly slideStopItems = computed(() =>
    this.slideCandidates().map((stop) => ({ id: stop.id, label: `${stop.name} · ${stop.value ?? 'no number'}` })));
  readonly slideStopIds = computed(() => this.node()?.slide?.stopIds ?? []);
  readonly slideFollowerItems = computed(() =>
    this.slideFollowerCandidates().map((item) => ({ id: item.id, label: item.name })));
  readonly slideFollowerIds = computed(() => this.node()?.slide?.followerIds ?? []);

  setSlideStops(ids: string[] | null): void {
    const slide = this.node()?.slide;
    if (!slide) return;
    const stopIds = ids ?? [];
    // A stop is never also a follower.
    const followerIds = (slide.followerIds ?? []).filter((id) => !stopIds.includes(id));
    this.patch({ slide: { ...slide, stopIds, followerIds } }, 'Move with value');
  }

  setSlideOverlap(mode: SlideOverlapMode): void {
    const slide = this.node()?.slide;
    if (slide) this.patch({ slide: { ...slide, avoidOverlap: mode } }, 'Move with value');
  }

  // ── Show at levels ─────────────────────────────────────────────────────────

  /** Zone sliders on the design - what "Show at levels" follows. */
  readonly levelSliderOptions = computed<LabelValueOption[]>(() =>
    widgetNodes(this.doc.document())
      .filter((node) => node.widget?.layoutType === 'ZONE_SLIDER')
      .map((node) => ({ label: node.name || 'Zone slider', value: node.id })),
  );
  /** The followed slider's level now (live). */
  readonly currentLevel = computed(() => this.runtime.zoneSliderLevel(this.node()?.showAtLevels?.sliderId ?? ''));
  /** Its levels, plus any ticked one it no longer has (so it can be unticked). */
  readonly levelChoices = computed(() => [...new Set([...(this.currentLevel()?.levels ?? []), ...(this.node()?.showAtLevels?.levels ?? [])])]);

  /** "Text by level": the followed slider's level now, and every level it has (plus any typed for a level it lost). */
  readonly levelTextNow = computed(() => this.runtime.zoneSliderLevel(this.node()?.levelTexts?.sliderId ?? ''));
  readonly levelTextLevels = computed(() => [...new Set([...(this.levelTextNow()?.levels ?? []), ...Object.keys(this.node()?.levelTexts?.texts ?? {})])]);
  trackLevel = (_: number, level: string) => level;

  toggleLevelTexts(on: boolean): void {
    this.patch({ levelTexts: on ? { texts: {} } : undefined }, 'Text by level');
  }
  setLevelText(level: string, text: string): void {
    const rule = this.node()?.levelTexts;
    if (!rule) return;
    const texts = { ...rule.texts };
    if (text.trim()) texts[level] = text; else delete texts[level];
    this.patch({ levelTexts: { ...rule, texts } }, 'Text by level');
  }
  setLevelTextsSlider(sliderId: string): void {
    const rule = this.node()?.levelTexts;
    if (!rule) return;
    this.patch({ levelTexts: { texts: rule.texts, ...(sliderId ? { sliderId } : {}) } }, 'Text by level');
  }

  toggleShowAtLevels(on: boolean): void {
    this.patch({ showAtLevels: on ? { levels: this.currentLevel()?.label ? [this.currentLevel()!.label] : [] } : undefined }, 'Show at levels');
  }
  setShowAtLevels(levels: string[] | null): void {
    const rule = this.node()?.showAtLevels;
    if (!rule) return;
    this.patch({ showAtLevels: { ...rule, levels: levels ?? [] } }, 'Show at levels');
  }
  setShowAtLevelsSlider(sliderId: string): void {
    const rule = this.node()?.showAtLevels;
    if (!rule) return;
    this.patch({ showAtLevels: { levels: rule.levels, ...(sliderId ? { sliderId } : {}) } }, 'Show at levels');
  }

  setSlideFollowers(ids: string[] | null): void {
    const slide = this.node()?.slide;
    if (!slide) return;
    this.patch({ slide: { ...slide, followerIds: ids ?? [] } }, 'Move with value');
  }

  toggleSlideFollower(id: string, checked: boolean): void {
    const slide = this.node()?.slide;
    if (!slide) return;
    const followerIds = (slide.followerIds ?? []).filter((followerId) => followerId !== id);
    this.patch({ slide: { ...slide, followerIds: checked ? [...followerIds, id] : followerIds } }, 'Move with value');
  }

  /** Siblings that can be stops, with the number each one reads as. Ones
   *  with a number are listed first — those are the usable markers. */
  readonly slideCandidates = computed(() => {
    const node = this.node();
    if (!node?.slide) return [];
    const stopIds = new Set(node.slide.stopIds);
    return (locateNode(this.doc.nodes(), node.id)?.siblings ?? [])
      .filter((sibling) => sibling.id !== node.id)
      .map((sibling) => ({ id: sibling.id, name: sibling.name, value: nodeNumber(sibling, this.readBound, this.readFormula), checked: stopIds.has(sibling.id) }))
      .sort((a, b) => Number(b.value !== null) - Number(a.value !== null));
  });

  /** Ticked stops that read as a number — it needs two to move between. */
  readonly slideUsableStops = computed(() => this.slideCandidates().filter((stop) => stop.checked && stop.value !== null).length);

  toggleSlide(on: boolean): void {
    this.patch({ slide: on ? { stopIds: [] } : undefined }, 'Move with value');
  }

  toggleSlideStop(id: string, checked: boolean): void {
    const slide = this.node()?.slide;
    const stopIds = (slide?.stopIds ?? []).filter((stopId) => stopId !== id);
    // A stop is never also a follower.
    const followerIds = (slide?.followerIds ?? []).filter((followerId) => followerId !== id);
    this.patch({ slide: { stopIds: checked ? [...stopIds, id] : stopIds, followerIds } }, 'Move with value');
  }

  // ── Edits ──────────────────────────────────────────────────────────────────

  private patch(patch: Partial<DesignNode>, label: string): void {
    const node = this.node();
    if (node) this.doc.updateNode(node.id, patch, label);
  }

  setGeometry(key: 'x' | 'y' | 'width' | 'height', value: number): void {
    if (!Number.isFinite(value)) return;
    const min = key === 'width' || key === 'height' ? 1 : -Infinity;
    const next = Math.max(min, roundGeometry(value));
    const node = this.node();
    // Groups and popup screens rescale their contents with the box.
    if ((node?.type === 'GROUP' || (node && this.doc.isPopupFrame(node.id))) && (key === 'width' || key === 'height')) {
      this.doc.resizeNode(
        node.id,
        {
          x: node.x,
          y: node.y,
          width: key === 'width' ? next : node.width,
          height: key === 'height' ? next : node.height,
        },
        undefined,
        `Set ${key}`,
      );
      return;
    }
    this.patch({ [key]: next }, `Set ${key}`);
  }

  /** Screen panel W / H — the page's size, applied on Enter / blur. */
  setScreenSize(side: 'width' | 'height', raw: unknown): void {
    const node = this.node();
    const value = Number(raw);
    if (!node || !Number.isFinite(value) || value <= 0) return;
    const min = side === 'width' ? 320 : 200;
    this.doc.resizeScreen(node.id, { [side]: Math.max(min, Math.round(value)) });
  }

  /** Hand that side back to automatic. */
  resetScreenSize(side: 'width' | 'height'): void {
    const node = this.node();
    if (node) this.doc.resizeScreen(node.id, { [side]: null });
  }

  setRotation(value: number): void {
    if (Number.isFinite(value)) this.patch({ rotation: Math.round(value) }, 'Set rotation');
  }

  /** A line's orientation IS its geometry — the longer side is the one it runs
   *  along — so this reports what the node already is rather than storing a
   *  separate flag that could disagree with it after a resize. */
  readonly lineOrientation = computed<'horizontal' | 'vertical' | 'diagonal'>(() => {
    const node = this.node();
    if (!node) return 'horizontal';
    if (Math.round(node.rotation ?? 0) % 180 !== 0) return 'diagonal';
    return (node.height ?? 0) > (node.width ?? 0) ? 'vertical' : 'horizontal';
  });

  /** Swaps the long and short sides so the line keeps the length it was drawn at,
   *  and clears any rotation — picking Horizontal after rotating should give a
   *  genuinely horizontal line, not one that is merely re-proportioned. */
  setLineOrientation(orientation: 'horizontal' | 'vertical' | 'diagonal'): void {
    const node = this.node();
    if (!node) return;
    if (orientation === 'diagonal') {
      this.patch({ rotation: 45 }, 'Set line orientation');
      return;
    }
    const length = Math.max(node.width ?? 1, node.height ?? 1);
    const thickness = Math.max(1, Math.min(node.width ?? 1, node.height ?? 1));
    this.patch(
      orientation === 'vertical'
        ? { width: thickness, height: length, rotation: 0 }
        : { width: length, height: thickness, rotation: 0 },
      'Set line orientation',
    );
  }

  setOpacity(percent: number): void {
    const clamped = Math.min(100, Math.max(0, percent || 0));
    this.patch({ opacity: clamped / 100 }, 'Set opacity');
  }

  setName(name: string): void {
    this.patch({ name }, 'Rename');
  }

  // Style — each merges into the existing style rather than replacing it.
  private patchStyle(patch: Partial<DesignNode['style']>, label: string): void {
    const node = this.node();
    if (node) this.doc.updateNode(node.id, { style: { ...node.style, ...patch } }, label);
  }

  setFillColor(color: string): void {
    this.patchStyle({ fill: { kind: 'solid', color } }, 'Set fill');
  }

  setFillKind(kind: Fill['kind']): void {
    const current = this.fill();
    this.patchStyle(
      { fill: kind === 'solid' ? { kind, color: current.color ?? '#ffffff' } : { kind } },
      'Set fill',
    );
  }

  setRadius(value: number): void {
    this.patchStyle({ radius: Math.max(0, value || 0) }, 'Set radius');
  }

  toggleStroke(enabled: boolean): void {
    this.patchStyle(
      { stroke: enabled ? { color: '#d0d5dd', width: 1, style: 'solid' } : null },
      'Toggle stroke',
    );
  }

  /** 0–100, or undefined (= 100) for an empty / invalid entry. */
  clampPercent(raw: unknown): number | undefined {
    const value = Number(raw);
    if (raw === '' || raw === null || !Number.isFinite(value)) return undefined;
    return Math.min(100, Math.max(0, Math.round(value)));
  }

  setStroke(patch: Partial<Stroke>): void {
    const current = this.stroke() ?? { color: '#d0d5dd', width: 1, style: 'solid' as const };
    this.patchStyle({ stroke: { ...current, ...patch } }, 'Set stroke');
  }

  toggleShadow(enabled: boolean): void {
    this.patchStyle(
      { shadow: enabled ? { x: 0, y: 2, blur: 8, spread: 0, color: 'rgba(16,24,40,0.12)' } : null },
      'Toggle shadow',
    );
  }

  setShadow(patch: Partial<Shadow>): void {
    const current = this.shadow();
    if (!current) return;
    this.patchStyle({ shadow: { ...current, ...patch } }, 'Set shadow');
  }

  setBlur(value: number): void {
    this.patchStyle({ blur: Math.max(0, value || 0) }, 'Set blur');
  }

  setOverflow(hidden: boolean): void {
    this.patchStyle({ overflow: hidden ? 'hidden' : 'visible' }, 'Set clipping');
  }

  // Auto layout
  setLayout(patch: Partial<AutoLayout>): void {
    const node = this.node();
    if (!node?.layout) return;
    this.doc.updateNode(node.id, { layout: { ...node.layout, ...patch } }, 'Auto layout');
  }

  setLayoutPadding(value: number): void {
    const padding = Math.max(0, value || 0);
    this.setLayout({ padding: [padding, padding, padding, padding] });
  }

  // Typography
  setText(patch: Partial<TextContent>): void {
    const node = this.node();
    if (!node?.text) return;
    this.doc.updateNode(node.id, { text: { ...node.text, ...patch } }, 'Typography');
  }

  // Icon
  readonly icon = computed(() => this.node()?.icon ?? null);
  readonly iconActionOptions = ICON_ACTION_OPTIONS;
  readonly iconUploadTargets = computed(() => widgetNodes(this.doc.document())
    .filter((node) => node.widget?.layoutType === 'FILE_UPLOAD'));
  readonly iconData = computed(() => iconDataFor(this.icon()?.name ?? ''));
  /** Icons are kept square, so one number drives both W and H. */
  readonly iconSize = computed(() => roundGeometry(this.node()?.width ?? 0));

  setIcon(patch: Partial<NonNullable<DesignNode['icon']>>): void {
    const node = this.node();
    if (!node?.icon) return;
    this.doc.updateNode(node.id, { icon: { ...node.icon, ...patch } }, 'Icon');
  }

  setIconInfo(patch: Partial<NonNullable<NonNullable<DesignNode['icon']>['info']>>): void {
    this.setIcon({ info: { source: 'static', title: 'Information', text: '', url: '', responsePath: '', ...this.icon()?.info, ...patch } });
  }

  /** Resize the node itself — an icon's "size" IS its box. */
  setIconSize(size: number): void {
    const next = Math.max(8, roundGeometry(size || 0));
    this.patch({ width: next, height: next }, 'Icon size');
  }

  /** Lucide's `fill` is a colour or the string `'none'`. */
  toggleIconFill(enabled: boolean): void {
    this.setIcon({ fill: enabled ? this.icon()?.color ?? '#344054' : 'none' });
  }

  readonly iconFilled = computed(() => {
    const fill = this.icon()?.fill;
    return !!fill && fill !== 'none';
  });

  // Image
  setImage(patch: Partial<NonNullable<DesignNode['image']>>): void {
    const node = this.node();
    if (!node?.image) return;
    this.doc.updateNode(node.id, { image: { ...node.image, ...patch } }, 'Image');
  }

  /** Inline the picked file as a data URI so the design stays self-contained. */
  onImageFile(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => this.setImage({ src: String(reader.result ?? '') });
    reader.readAsDataURL(file);
  }

  // ── Design tier: variant, orientation and the JSON format controls ────────

  readonly appearance = computed<Record<string, unknown>>(
    () => this.node()?.widget?.appearance ?? {},
  );

  /**
   * The selected widget's layout type as a **plain string**.
   *
   * Every control-list computed below derives from THIS rather than from
   * `node()`. `node()` is a fresh object after each commit, so deriving from it
   * rebuilt the control arrays on every keystroke; `*ngFor` then tore down and
   * recreated each row, and the input you were typing in lost focus. A string
   * compares equal, so the lists stay referentially stable while you type.
   */
  readonly widgetLayoutType = computed(() => this.node()?.widget?.layoutType ?? '');

  // ── Text ↔ data binding ────────────────────────────────────────────────────
  protected readonly dataService = inject(DesignDataService);
  private readonly runtime = inject(DesignRuntimeService);

  /** Filters that target the selected text. */
  readonly textFilters = computed(() => {
    const node = this.node();
    return node?.type === 'TEXT' ? this.runtime.filtersOf(node.id) : [];
  });

  /** Generated filters / cross-filter charts not yet linked to this text. */
  readonly availableTextFilters = computed(() => {
    const linked = new Set(this.textFilters().map((filter) => filter.nodeId));
    return this.runtime.textFilterSources().filter((source) => !linked.has(source.nodeId));
  });

  linkTextFilter(filterNodeId: string): void {
    const node = this.node();
    if (filterNodeId && node) this.runtime.setTextTarget(filterNodeId, node.id, true);
  }

  unlinkTextFilter(filterNodeId: string): void {
    const node = this.node();
    if (node) this.runtime.setTextTarget(filterNodeId, node.id, false);
  }
  readonly valueToken = '{{value}}';
  readonly scaleOptions: ReadonlyArray<{ value: NumberScale; label: string }> = [
    { value: 'auto', label: 'Auto (K/M/B)' },
    { value: 'none', label: 'None' },
    { value: 'K', label: 'Thousands (K)' },
    { value: 'M', label: 'Millions (M)' },
    { value: 'B', label: 'Billions (B)' },
  ];

  // ── ng-select rows for the pickers that used to be native <select>s ──
  // A native popup is sized to its longest option and spilled past the
  // panel. Each list folds its "none" row in, and is a computed / readonly
  // field so the `[items]` reference only changes when the data does
  // (ng-select reloads its option list on every new reference).
  readonly sliderChoices = computed<{ value: string; label: string }[]>(() => [
    { value: '', label: 'First zone slider' },
    ...this.levelSliderOptions(),
  ]);
  readonly slideOverlapOptions: { value: string; label: string }[] = [
    { value: 'nearest', label: 'Slide to nearest free spot (stops stay)' },
    { value: 'beside', label: 'Move beside it (no overlap)' },
    { value: 'above', label: 'Move above it' },
    { value: 'below', label: 'Move below it' },
    { value: 'none', label: 'Allow overlap' },
  ];
  readonly datasetChoices = computed<{ value: string; label: string }[]>(() => {
    const current = this.binding()?.dataset ?? '';
    const names = this.datasetOptions();
    return [
      { value: '', label: '— Not linked —' },
      ...(current && !names.includes(current) ? [{ value: current, label: current }] : []),
      ...names.map((name) => ({ value: name, label: name })),
    ];
  });
  readonly columnChoices = computed<{ value: string; label: string }[]>(() => {
    const current = this.binding()?.field ?? '';
    const columns = this.columnOptions();
    return [
      ...(current && !columns.includes(current) ? [{ value: current, label: current }] : []),
      ...columns.map((column) => ({ value: column, label: column })),
    ];
  });
  readonly statusChoiceOptions = computed<{ value: string; label: string }[]>(() => {
    const bound = this.binding();
    const current = bound?.statusValue ?? '';
    const choices = this.dataService.statusChoices(bound);
    return [
      { value: '', label: '— Select —' },
      ...(current && !choices.includes(current) ? [{ value: current, label: current }] : []),
      ...choices.map((status) => ({ value: status, label: status })),
    ];
  });
  readonly valueColorTargetOptions: { value: string; label: string }[] = [
    { value: 'text', label: 'The text' },
    { value: 'pill', label: 'The pill behind it (text turns white)' },
  ];
  readonly valueColorSourceChoices = computed<{ id: string; label: string }[]>(() => [
    { id: '', label: "This text's own number" },
    ...this.valueColorMarkers(),
  ]);
  readonly valueColorLimitChoices = computed<{ id: string; label: string }[]>(() => [
    { id: '', label: 'Number' },
    ...this.valueColorMarkers(),
    ...this.valueColorColumnChoices(),
  ]);

  /**
   * Where a "Color by value" column limit is read from, with the aggregation it
   * is read with - so the limit is measured like the value (one record's SUM vs
   * SUM …). First the compared text's own dataset (the Compare source, else this
   * text); when that number has none (a formula, a marker), the datasets of the
   * bound texts beside it, then of any bound text on the screen.
   */
  private readonly valueColorReferences = computed<Array<{ dataset: string; aggregation: string }>>(() => {
    const node = this.node();
    if (!node) return [];
    const siblings = (locateNode(this.doc.nodes(), node.id)?.siblings ?? []).filter((sibling) => sibling.id !== node.id);
    const source = node.valueColorSourceId ? siblings.find((sibling) => sibling.id === node.valueColorSourceId) : node;
    const aggregationOf = (binding: { aggregation?: string } | undefined) =>
      ['SUM', 'AVG', 'MIN', 'MAX', 'COUNT'].includes(String(binding?.aggregation)) ? String(binding!.aggregation) : 'SUM';
    const own = [source?.dataBinding, node.dataBinding].find((binding) => !!binding?.dataset);
    const candidates = own
      ? [own]
      : [...siblings.map((sibling) => sibling.dataBinding), ...[...boundTextComponents(this.doc.document()).values()].map((text) => text.dataBinding)];
    const seen = new Set<string>();
    return candidates
      .filter((binding): binding is NonNullable<typeof binding> => !!binding?.dataset && !seen.has(binding.dataset) && !!seen.add(binding.dataset))
      .map((binding) => ({ dataset: binding.dataset!, aggregation: aggregationOf(binding) }));
  });

  /** Their columns, listed once loaded. */
  private readonly loadValueColorColumns = effect(() => {
    this.valueColorReferences().forEach((reference) => this.dataService.loadColumns(reference.dataset));
  });

  /** A limit can be a dataset column: its value for the current filters (each record's own floor, target …). */
  readonly valueColorColumnChoices = computed<{ id: string; label: string }[]>(() => {
    const references = this.valueColorReferences();
    return references.flatMap((reference) => {
      const state = this.dataService.columns(reference.dataset);
      if (state.status !== 'ready') return [];
      return state.value.map((field) => ({
        id: columnLimitId(reference.dataset, field, reference.aggregation),
        label: references.length > 1 ? `Column: ${field} (${reference.dataset})` : `Column: ${field}`,
      }));
    });
  });
  readonly iconActionChoices = [...ICON_ACTION_OPTIONS];
  readonly uploadTargetChoices = computed<{ value: string; label: string }[]>(() => [
    { value: '', label: 'Choose a file upload control' },
    ...this.iconUploadTargets().map((target) => ({ value: target.id, label: target.name })),
  ]);

  /** "+ Link a filter…" is an action, not a value: link the pick, then clear
   *  the control back to its placeholder. ng-select's (change) carries the
   *  row object (or, after the clear, null — ignored). */
  onLinkFilterPick(picked: { nodeId: string } | string | null | undefined, select: { clearModel(): void }): void {
    const nodeId = typeof picked === 'string' ? picked : picked?.nodeId ?? '';
    if (!nodeId) return;
    this.linkTextFilter(nodeId);
    select.clearModel();
  }

  readonly binding = computed<TextDataBinding | null>(() =>
    this.node()?.type === 'TEXT' ? this.node()?.dataBinding ?? null : null);

  readonly datasetOptions = computed(() => {
    const state = this.dataService.datasets();
    return state.status === 'ready' ? state.value : [];
  });

  private readonly columnState = computed(() => {
    const dataset = this.binding()?.dataset;
    return dataset ? this.dataService.columns(dataset) : null;
  });

  readonly columnOptions = computed(() => {
    const state = this.columnState();
    return state?.status === 'ready' ? state.value : [];
  });

  readonly columnPlaceholder = computed(() => {
    const state = this.columnState();
    if (state?.status === 'loading') return 'Loading columns…';
    if (state?.status === 'error') return 'Could not load columns';
    return 'Select a column';
  });

  readonly boundPreview = computed<{ label: string; error: boolean }>(() => {
    const binding = this.binding();
    if (!binding?.field) return { label: 'Pick a column to show its value.', error: false };
    const state = this.dataService.value(binding, this.node()?.id ?? '');
    switch (state.status) {
      case 'ready': return { label: `Value: ${formatBoundValue(binding, state.value)}`, error: false };
      case 'error': return { label: state.message, error: true };
      default: return { label: 'Loading value…', error: false };
    }
  });

  private readonly loadDataEffect = effect(() => {
    if (this.node()?.type !== 'TEXT') return;
    this.dataService.loadDatasets();
    const dataset = this.binding()?.dataset;
    if (dataset) this.dataService.loadColumns(dataset);
  });

  hasDataset(name: string): boolean {
    return this.datasetOptions().includes(name);
  }

  hasColumn(name: string): boolean {
    return this.columnOptions().includes(name);
  }

  setBindingDataset(dataset: string): void {
    if (!dataset) {
      this.unbindText();
      return;
    }
    const current = this.binding();
    if (current?.dataset === dataset) return;
    this.writeBinding({ ...(current ?? { aggregation: 'SUM' }), dataset, field: '' });
  }

  // ── Value formula (input box / slider / zone slider) ──────────────────────

  readonly isInputValueWidget = computed(() => {
    const node = this.node();
    return !!node && isInputWidget(node);
  });

  /** The selected input's value formula; null when it has none (toggle off). */
  readonly valueFormula = computed<string | null>(() => {
    const node = this.node();
    if (!node || !isInputWidget(node)) return null;
    const formula = node.widget?.appearance?.[VALUE_FORMULA_KEY];
    return typeof formula === 'string' ? formula : null;
  });

  readonly valueFormulaSources = computed(() => {
    const node = this.node();
    return node && this.valueFormula() !== null ? this.dataService.formulaSources(node.id) : [];
  });

  readonly valueFormulaPreview = computed<{ label: string; error: boolean }>(() => {
    const expression = this.valueFormula();
    const node = this.node();
    if (expression === null || !node) return { label: '', error: false };
    if (!expression.trim()) return { label: 'Write a formula, e.g. first([Future Volumes], [Current Volumes]).', error: false };
    const result = this.dataService.formula({ expression }, node.id);
    switch (result.status) {
      case 'ready': return { label: `Value now: ${result.value.toLocaleString('en-US')}`, error: false };
      case 'error': return { label: result.message, error: true };
      default: return { label: 'Value now: — (the inputs it uses are empty)', error: false };
    }
  });

  toggleValueFormula(on: boolean): void {
    this.writeValueFormula(on ? '' : null);
  }

  setValueFormula(expression: string): void {
    this.writeValueFormula(expression ?? '');
  }

  insertValueFormulaRef(name: string): void {
    const current = this.valueFormula() ?? '';
    const spacer = current && !/[\s(,]$/.test(current) ? ' ' : '';
    this.writeValueFormula(`${current}${spacer}[${name}]`);
  }

  /** Stored on the widget's appearance; null removes it. */
  private writeValueFormula(expression: string | null): void {
    const node = this.node();
    if (!node?.widget) return;
    const appearance = { ...node.widget.appearance };
    if (expression === null) delete appearance[VALUE_FORMULA_KEY];
    else appearance[VALUE_FORMULA_KEY] = expression;
    this.doc.updateNode(node.id, { widget: { ...node.widget, appearance } }, 'Value formula');
  }

  // ── Formula ────────────────────────────────────────────────────────────────

  readonly formula = computed<TextFormula | null>(() =>
    this.node()?.type === 'TEXT' ? this.node()?.formula ?? null : null);

  /** Inputs and texts the formula can reference by name. */
  readonly formulaSources = computed(() => {
    const node = this.node();
    return node && this.formula() ? this.dataService.formulaSources(node.id) : [];
  });

  readonly formulaPreview = computed<{ label: string; error: boolean }>(() => {
    const formula = this.formula();
    const node = this.node();
    if (!formula || !node) return { label: '', error: false };
    if (!formula.expression.trim()) return { label: 'Write a formula, e.g. [Current Volumes] * [Price].', error: false };
    const result = this.dataService.formula(formula, node.id);
    switch (result.status) {
      case 'ready': return { label: `Result: ${formatBoundValue(formula, result.value)}`, error: false };
      case 'error': return { label: result.message, error: true };
      default: return { label: 'Result: — (an input is still empty)', error: false };
    }
  });

  // ── Color by value ───────────────────────────────────────────────────────

  /** The selected text's number right now (formula, live value, or typed). */
  readonly valueColorPreview = computed<number | null>(() => {
    const node = this.node();
    if (!node || node.type !== 'TEXT') return null;
    if (node.formula?.expression) {
      const result = this.dataService.formula(node.formula, node.id);
      return result.status === 'ready' ? result.value : null;
    }
    if (node.dataBinding) {
      const state = this.dataService.value(node.dataBinding);
      return state.status === 'ready' ? numericValue(state.value) : null;
    }
    return numericValue(node.text?.value ?? '');
  });

  /** On: three ranges to fill in (low / middle / high); off: removes them. */
  /** Texts beside this one that read as a number - limits a "Color by value" range can follow. */
  readonly valueColorMarkers = computed(() => {
    const node = this.node();
    if (!node) return [];
    return (locateNode(this.doc.nodes(), node.id)?.siblings ?? [])
      .filter((sibling) => sibling.id !== node.id)
      .map((sibling) => ({ id: sibling.id, value: nodeNumber(sibling, this.readBound, this.readFormula), name: sibling.name }))
      .filter((marker) => marker.value !== null)
      .map((marker) => ({ id: marker.id, label: `${marker.name} · ${marker.value}` }));
  });

  /** "Initials of": the other texts on this screen whose value the initials come from. */
  readonly initialsSourceChoices = computed<{ id: string; label: string }[]>(() => {
    const node = this.node();
    if (!node || node.type !== 'TEXT') return [];
    return [
      { id: '', label: 'Off - show what is typed' },
      ...flattenNodes(this.doc.nodes())
        .filter((candidate) => candidate.type === 'TEXT' && candidate.id !== node.id)
        .map((candidate) => ({
          id: candidate.id,
          label: candidate.dataBinding ? `${candidate.name} · ${candidate.dataBinding.field}` : candidate.name,
        })),
    ];
  });

  setInitialsSource(id: string): void {
    this.patch({ initialsOfId: id || undefined }, 'Initials of');
  }

  setValueColorSource(id: string): void {
    this.patch({ valueColorSourceId: id || undefined }, 'Color by value');
  }

  setValueColorTarget(target: string): void {
    this.patch({ valueColorTarget: target === 'pill' ? 'pill' : undefined }, 'Color by value');
  }

  toggleValueColors(on: boolean): void {
    this.patch({ valueColors: on ? [
      { to: 33, color: '#d92d20' },
      { from: 33, to: 66, color: '#f79009' },
      { from: 66, color: '#12b76a' },
    ] : undefined }, 'Color by value');
  }

  addValueColor(): void {
    const ranges = this.node()?.valueColors ?? [];
    const last = ranges[ranges.length - 1];
    const from = last?.to ?? last?.from ?? null;
    this.patch({ valueColors: [...ranges, { from, color: '#2970ff' }] }, 'Color by value');
  }

  patchValueColor(index: number, patch: Partial<ValueColorRange>): void {
    const ranges = [...(this.node()?.valueColors ?? [])];
    if (!ranges[index]) return;
    ranges[index] = { ...ranges[index], ...patch };
    this.patch({ valueColors: ranges }, 'Color by value');
  }

  removeValueColor(index: number): void {
    const ranges = (this.node()?.valueColors ?? []).filter((_, i) => i !== index);
    this.patch({ valueColors: ranges.length ? ranges : undefined }, 'Color by value');
  }

  /** A From / To box: empty = no limit. */
  rangeNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  trackByIndex(index: number): number {
    return index;
  }

  toggleFormula(on: boolean): void {
    const node = this.node();
    if (!node?.text) return;
    if (!on) {
      this.patch({ formula: undefined }, 'Formula');
      return;
    }
    // Make sure the result shows: a text without the token gets one.
    const value = node.text.value.includes(this.valueToken) ? node.text.value : this.valueToken;
    this.patch({ formula: { expression: '' }, text: { ...node.text, value } }, 'Formula');
  }

  patchFormula(patch: Partial<TextFormula>): void {
    const current = this.formula();
    if (current) this.patch({ formula: { ...current, ...patch } }, 'Formula');
  }

  insertFormulaRef(name: string): void {
    const current = this.formula()?.expression ?? '';
    const spacer = current && !/[\s(]$/.test(current) ? ' ' : '';
    this.patchFormula({ expression: `${current}${spacer}[${name}]` });
  }

  trackFormulaSource(_: number, node: DesignNode): string {
    return node.id;
  }

  patchBinding(patch: Partial<TextDataBinding>): void {
    const current = this.binding();
    if (current) this.writeBinding({ ...current, ...patch });
  }

  /** Unlinking keeps what the text currently shows instead of a bare token. */
  unbindText(): void {
    const node = this.node();
    const binding = this.binding();
    if (!node || !binding || !node.text) return;
    const state = binding.field ? this.dataService.value(binding, node.id) : null;
    const shown = state?.status === 'ready' ? formatBoundValue(binding, state.value) : '';
    this.doc.updateNode(node.id, {
      dataBinding: undefined,
      text: { ...node.text, value: node.text.value.split(this.valueToken).join(shown) },
    }, 'Unlink text from data');
  }

  private writeBinding(binding: TextDataBinding): void {
    const node = this.node();
    if (!node?.text) return;
    // A text with no token becomes just the value — its old content was a placeholder.
    const value = node.text.value.includes(this.valueToken) ? node.text.value : this.valueToken;
    this.doc.updateNode(node.id, { dataBinding: binding, text: { ...node.text, value } }, 'Text data');
  }

  // ── Nested table: per-level / per-column customisation ─────────────────────
  readonly isNestedTable = computed(() => this.widgetLayoutType() === 'NESTED_TABLE');
  /** The data exactly as the canvas renders it, so the editor lists real columns. */
  readonly nestedTableData = computed(() => {
    const widget = this.node()?.widget;
    return this.isNestedTable() && widget ? resolveWidgetData(widget) : null;
  });
  readonly nestedTableOverrides = computed<NestedTableLevelOverride[]>(() => {
    const value = this.appearance()['levelOverrides'];
    return Array.isArray(value) ? (value as NestedTableLevelOverride[]) : [];
  });

  onNestedOverridesChange(levelOverrides: NestedTableLevelOverride[]): void {
    this.setAppearance({ levelOverrides });
  }

  // ── Table & Matrix: per-column generic cell configuration ──────────────────
  readonly isTableMatrix = computed(() => this.widgetLayoutType() === 'TABLE_MATRIX');
  /** The data exactly as the canvas renders it, so the editor lists real columns. */
  readonly tableMatrixData = computed(() => {
    const widget = this.node()?.widget;
    return this.isTableMatrix() && widget ? resolveWidgetData(widget) : null;
  });
  readonly tableColumnCellConfigs = computed<TableColumnCellConfigs>(() => {
    const value = this.appearance()[COLUMN_CELL_CONFIGS_KEY];
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as TableColumnCellConfigs) : {};
  });
  onTableColumnCellConfigsChange(configs: TableColumnCellConfigs): void {
    this.setAppearance({ [COLUMN_CELL_CONFIGS_KEY]: configs });
  }

  // ── Zone slider: grouped settings + zone list (its own section) ───────────
  readonly isZoneSlider = computed(() => this.widgetLayoutType() === 'ZONE_SLIDER');
  /** The Data tab maps zones from rows → the inspector's list is a fallback. */
  readonly zoneSliderZonesFromData = computed(
    () => this.isZoneSlider() && zoneSliderBindingOf(this.node()?.widget?.binding).zonesMode === 'rows',
  );

  // ── Button: feature picker + that feature's settings (its own section) ────
  readonly isButton = computed(() => this.widgetLayoutType() === 'BUTTON');
  reorderUpload(command: 'front' | 'back'): void {
    const node = this.node();
    if (node?.widget?.layoutType === 'FILE_UPLOAD') this.doc.reorder(node.id, command);
  }
  readonly isFileUpload = computed(() => this.widgetLayoutType() === 'FILE_UPLOAD');
  readonly fileUploadConfig = computed<DashboardTileFileUpload>(() => {
    const widget = this.node()?.widget;
    const stored = widget?.build?.['fileUploadControl'] as DashboardTileFileUpload | undefined;
    const data = (widget ? resolveWidgetData(widget) : null) ?? {};
    return stored ? { ...stored, buttonLabel: String(data['buttonLabel'] ?? stored.buttonLabel), accept: String(data['accept'] ?? stored.accept) } : { buttonLabel: String(data['buttonLabel'] ?? 'Upload file'), accept: String(data['accept'] ?? '.xlsx,.xls,.csv'), targets: [], widgetData: data };
  });
  onFileUploadConfigChange(control: DashboardTileFileUpload): void {
    const node = this.node();
    if (!node?.widget) return;
    const build = node.widget.build ?? {};
    const targets = control.targets.map(componentId => ({ componentId, label: componentId }));
    const data = { ...control.widgetData, buttonLabel: control.buttonLabel, accept: control.accept, targets };
    const widget = { ...node.widget, data, appearance: { ...node.widget.appearance, buttonLabel: control.buttonLabel, accept: control.accept },
      build: { ...build, componentId: node.widget.componentId ?? build['componentId'] ?? node.id,
        payload: { componentId: node.widget.componentId ?? build['componentId'] ?? node.id, componentType: 'INPUT', inputType: 'FILE_UPLOAD', ...((build['payload'] as Record<string, unknown>) ?? {}), buttonLabel: control.buttonLabel, accept: control.accept, targets, apiConfig: control.apiConfig },
        fileUploadControl: { ...control, widgetData: data } },
    };
    this.runtime.forget(node.id);
    this.doc.updateNode(node.id, { widget }, 'File upload settings');
  }


  /** Generated charts / tables / bound texts a button can filter or refresh. */
  /** A Button widget, or a table whose cells can hold buttons: both need the button option lists. */
  readonly hostsButtons = computed(() => this.isButton() || this.isTableMatrix() || this.isNestedTable());

  readonly buttonTargetOptions = computed<LabelValueOption[]>(() =>
    this.hostsButtons()
      ? chartTargets(this.doc.document(), '', '').map((target) => ({ label: target.label || target.componentId, value: target.componentId }))
      : [],
  );

  private readonly otherWidgets = computed(() => {
    const id = this.node()?.id;
    return this.hostsButtons() ? widgetNodes(this.doc.document()).filter((node) => node.id !== id) : [];
  });

  /** Charts and tables whose data a download button exports. */
  readonly buttonWidgetOptions = computed<LabelValueOption[]>(() =>
    this.otherWidgets()
      .filter((node) => WIDGET_PALETTE_META[node.widget!.layoutType]?.group !== 'inputs')
      .map((node) => ({ label: node.name || WIDGET_LABELS[node.widget!.layoutType] || node.id, value: node.id })),
  );

  /** Zone sliders, for a button's Approval feature. */
  readonly buttonSliderOptions = computed<LabelValueOption[]>(() =>
    this.otherWidgets()
      .filter((node) => node.widget!.layoutType === 'ZONE_SLIDER')
      .map((node) => ({ label: node.name || 'Zone slider', value: node.id })),
  );

  /** Input boxes, dropdowns, sliders and bound texts - a "Save changes" button sends their values. */
  readonly buttonValueSourceOptions = computed<LabelValueOption[]>(() =>
    this.hostsButtons() ? valueSourceOptionsOf(this.doc.document().pages.flatMap((page) => page.nodes)) : []);

  /** Each generated widget's dataset, by node id - a button's Export reads its columns. */
  readonly buttonWidgetDatasets = computed<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    this.otherWidgets().forEach((node) => {
      const tile = tileFromNode(node);
      const dataset = tile ? tileDataset(tile) : '';
      if (dataset) out[node.id] = dataset;
    });
    return out;
  });

  /** Everything a table column's buttons pick from (the same lists a Button widget gets). */
  readonly cellButtonContext = computed<CellButtonOptions>(() => ({
    options: this.configOptions(),
    targetOptions: this.buttonTargetOptions(),
    widgetOptions: this.buttonWidgetOptions(),
    popupOptions: this.buttonPopupOptions(),
    screenOptions: this.buttonScreenOptions(),
    sliderOptions: this.buttonSliderOptions(),
    approvalTableOptions: this.buttonApprovalTableOptions(),
    widgetDatasets: this.buttonWidgetDatasets(),
    dashboardOptions: this.buttonDashboardOptions(),
    valueSourceOptions: this.buttonValueSourceOptions(),
  }));

  /** Tables, for a button's Approval feature (one item per row). */
  readonly buttonApprovalTableOptions = computed<LabelValueOption[]>(() =>
    this.otherWidgets()
      .filter((node) => APPROVAL_TABLE_TYPES.has(node.widget!.layoutType))
      .map((node) => ({ label: `Table: ${node.name || WIDGET_LABELS[node.widget!.layoutType] || 'Table'}`, value: node.id })),
  );

  readonly buttonPopupOptions = computed<LabelValueOption[]>(() =>
    this.otherWidgets()
      .filter((node) => node.widget!.layoutType === 'POPUP')
      .map((node) => ({ label: node.name || 'Popup', value: node.id })),
  );

  readonly buttonScreenOptions = computed<LabelValueOption[]>(() =>
    this.hostsButtons() ? screensOf(this.doc.document()).map((screen) => ({ label: screen.name || screen.id, value: screen.id })) : [],
  );

  /**
   * A blank square frame for this button's popup, placed under everything on
   * the page and selected — design it like any screen. The button is pointed
   * at it straight away.
   */
  /** The root frame this button opens as its popup, if it exists. */
  readonly buttonPopupFrame = computed<DesignNode | null>(() => {
    const node = this.node();
    if (!this.isButton() || !node?.widget) return null;
    const config = buttonConfigOf(node.widget.appearance);
    if (!opensPopupScreen(config) || !config.popupScreenId) return null;
    return this.doc.nodes().find((item) => item.id === config.popupScreenId && item.type === 'FRAME') ?? null;
  });

  /** none = nothing to edit yet; shown = on the canvas; hidden = put away after Done. */
  readonly buttonPopupState = computed<'none' | 'shown' | 'hidden'>(() => {
    const frame = this.buttonPopupFrame();
    return !frame ? 'none' : frame.visible ? 'shown' : 'hidden';
  });

  /** The popup screen being edited: the selection is that frame or inside it. */
  readonly editingPopup = computed<{ frame: DesignNode } | null>(() => {
    const selected = this.node();
    if (!selected) return null;
    const document = this.doc.document();
    const popups = popupScreenIdsOf(document);
    const contains = (node: DesignNode): boolean => node.id === selected.id || !!node.children?.some(contains);
    const frame = this.doc.nodes().find((node) => popups.has(node.id) && contains(node));
    return frame ? { frame } : null;
  });

  /** "Update popup screen": put the frame back on the canvas, selected and in view. */
  openPopupScreen(): void {
    const frame = this.buttonPopupFrame();
    if (!frame) return;
    if (!frame.visible) this.doc.updateNode(frame.id, { visible: true }, 'Show popup screen');
    this.doc.select(frame.id);
    this.canvasView?.fitTo(frame);
  }

  /** "Done": hide the popup screen and go back to the plain canvas view. */
  finishPopupScreen(): void {
    const frameId = this.editingPopup()?.frame.id ?? this.buttonPopupFrame()?.id;
    if (!frameId) return;
    this.doc.updateNode(frameId, { visible: false }, 'Hide popup screen');
    this.doc.clearSelection();
    this.canvasView?.fitToWidth();
  }

  createPopupScreen(): void {
    const button = this.node();
    if (!button?.widget) return;
    const bounds = this.doc.contentBounds();
    const frame = createScreenFrame(480, 480);
    frame.name = `${button.name || 'Button'} popup`;
    frame.fluid = false;
    frame.popupScreen = true;
    frame.x = Math.max(0, roundGeometry(bounds.x));
    frame.y = Math.ceil(bounds.y + bounds.height + 80);
    frame.style = { ...frame.style, radius: 14 };
    this.setAppearance({ popupSource: 'screen', popupScreenId: frame.id });
    this.doc.addNode(frame);
    this.canvasView?.fitTo(frame);
  }

  /** Saved dashboards in this browser (same list the table / popup navigation offers). */
  readonly buttonDashboardOptions = computed<LabelValueOption[]>(() => {
    if (!this.hostsButtons()) return [];
    const saved = readJson<Array<{ name?: string; config?: Record<string, unknown> }>>(SAVED_DASHBOARDS_STORAGE_KEY, []);
    return (Array.isArray(saved) ? saved : [])
      .map((dashboard) => ({ label: dashboard.name || String(dashboard.config?.['dashboardId'] ?? ''), value: String(dashboard.config?.['dashboardId'] ?? '') }))
      .filter((option) => !!option.value);
  });

  readonly variantOptions = computed(() =>
    variantOptionsFor(this.layoutConfig(), this.widgetLayoutType()),
  );

  readonly variantLabel = computed(
    () => appearanceSpecFor(this.widgetLayoutType()).variantLabel ?? 'Variant',
  );

  readonly showOrientation = computed(
    () => appearanceSpecFor(this.widgetLayoutType()).orientation === true,
  );

  readonly orientationOptions = ORIENTATION_OPTIONS;

  /** Family-specific design controls (input controls' label/placeholder/range). */
  /** A control gated by `visibleWhen` (e.g. the stepper's step / min / max)
   *  shows only while the control it depends on has one of those values. */
  readonly specControls = computed(
    () => (appearanceSpecFor(this.widgetLayoutType()).controls ?? [])
      .filter((control) => formatControlVisible(control, this.appearance())),
  );

  /** Stable identity for the control ngFors, so typing never rebuilds a row. */
  trackSlideStop(_: number, stop: { id: string }): string {
    return stop.id;
  }

  trackByControlKey(_: number, control: { key: string }): string {
    return control.key;
  }

  trackByGroupName(_: number, group: FormatControlGroup): string {
    return group.name;
  }

  readonly currentVariant = computed(() =>
    currentVariant(this.appearance(), this.node()?.widget?.layoutType ?? ''),
  );

  readonly currentOrientation = computed(() => currentOrientation(this.appearance()));

  /** Hint declared on the chosen variant, e.g. how stacking gets its series. */
  readonly variantHint = computed(
    () =>
      variantByValue(this.layoutConfig(), this.node()?.widget?.layoutType ?? '', this.currentVariant())
        ?.hint ?? '',
  );

  /**
   * The format controls that apply to this chart type, grouped exactly as the
   * Layout Builder groups them — reusing its pure selectors rather than
   * restating the catalogue here.
   */
  /**
   * How many levels the selected table has, so per-level Format templates
   * ("Level {n}") repeat once per level — 6 levels, 6 groups. Read from the
   * rendered data, else the build form's levels, else the sample's count.
   */
  readonly formatLevelCount = computed(() => {
    const widget = this.node()?.widget;
    if (!widget) return DEFAULT_FORMAT_LEVELS;
    const lengthOf = (value: unknown) => (Array.isArray(value) ? value.length : 0);
    const payload = (widget.build?.['payload'] ?? {}) as Record<string, unknown>;
    const count = lengthOf(widget.data?.['levels']) || lengthOf(payload['levels']) || Number(widget.appearance?.['levelCount']) || 0;
    return count > 0 ? count : DEFAULT_FORMAT_LEVELS;
  });

  readonly formatGroups = computed<FormatControlGroup[]>(() => {
    const layout = this.layoutConfig();
    const layoutType = this.widgetLayoutType();
    if (!layout || !layoutType) return [];
    // The zone slider and button have no card header or axes; their own
    // settings sections cover everything the shared chart format controls
    // would offer (legend, axes, tooltips… none of which apply).
    if (NO_FORMAT_LAYOUTS.has(layoutType)) return [];

    // Card chrome (background, border, corner radius, header colours) is the
    // NODE's style here and is already edited in Appearance / Stroke / Shadow.
    // Two controls for the same pixels would just fight each other.
    // `visibleWhen` rules (a colour only with a visual picked …) apply here as
    // in the Layout Builder's Format popup.
    // A key the family's own settings section already edits is not repeated here.
    const specKeys = new Set((appearanceSpecFor(layoutType).controls ?? []).map((control) => control.key));
    const controls = applicableFormatControls(layout, layoutType, this.formatLevelCount()).filter(
      (control) => !NODE_LEVEL_FORMAT_KEYS.has(control.key) && !specKeys.has(control.key) && formatControlVisible(control, this.appearance()),
    );

    return groupFormatControls(controls, formatGroupOrderOf(layout)).filter(
      (group) => group.controls.length > 0,
    );
  });

  /** Option lists the select controls resolve their `optionsKey` against. */
  readonly configOptions = computed<Record<string, unknown>>(
    () => ({
      ...APPEARANCE_OPTIONS,
      ...((this.layoutConfig()?.options as Record<string, unknown>) ?? {}),
      // The widget's own columns, for `column-select` controls (checkbox column …).
      tableColumns: tableMatrixColumnsOf(this.tableMatrixData()).map((column) => ({ label: column.label, value: column.key })),
      // Each column's distinct values in the loaded rows, for `row-filter` controls.
      tableColumnValues: tableColumnValuesOf(this.tableMatrixData(), tableMatrixColumnsOf(this.tableMatrixData()).map((column) => column.key)),
    }),
  );

  /** Collapsed format groups, so the pane is not a wall of 60 controls. */
  private readonly openGroups = signal<ReadonlySet<string>>(new Set(['Title']));

  isGroupOpen(group: string): boolean {
    return this.openGroups().has(group);
  }

  toggleGroup(group: string): void {
    const next = new Set(this.openGroups());
    if (next.has(group)) next.delete(group);
    else next.add(group);
    this.openGroups.set(next);
  }

  /**
   * Merge a design-tier value onto the widget's `appearance`.
   *
   * While the widget is still on SAMPLE data, the sample is regenerated to
   * match — switching to "Stacked" has to produce series data, or the chart
   * would claim to be stacked while rendering a single flat bar.
   */
  setAppearance(patch: Record<string, unknown>): void {
    const node = this.node();
    if (!node?.widget) return;

    // Nested is a Table type with its own widget: switching to or from it
    // re-types the widget (with that type's dummy data) instead of restyling it.
    const retypeTo = tableRetypeFor(node.widget.layoutType, patch['variant']);
    if (retypeTo) {
      const appearance = { ...defaultAppearance(retypeTo), ...(retypeTo === 'TABLE_MATRIX' ? { variant: patch['variant'] } : {}) };
      const meta = variantByValue(this.layoutConfig(), retypeTo, appearance['variant']);
      const widget = withSampleData(
        { ...node.widget, layoutType: retypeTo, data: null, appearance, build: undefined, componentId: undefined },
        node.id,
        node.name,
        meta ?? {},
      );
      this.runtime.forget(node.id);
      this.doc.updateNode(node.id, { widget }, 'Change table type');
      return;
    }

    const appearance = { ...node.widget.appearance, ...patch };
    let widget: WidgetContent = { ...node.widget, appearance };

    if (widget.sample) {
      const meta = variantByValue(this.layoutConfig(), widget.layoutType, appearance['variant']);
      widget = withSampleData(widget, node.id, node.name, meta ?? {});
    }

    this.doc.updateNode(node.id, { widget }, 'Chart appearance');
  }

  onControlChange(change: { key: string; value: unknown }): void {
    this.setAppearance({ [change.key]: change.value });
  }

  /** True while the widget is showing generated placeholder data. */
  readonly isSample = computed(() => this.node()?.widget?.sample === true);

  // Canvas (nothing selected)
  setCanvas(patch: Partial<ReturnType<typeof this.canvas>>): void {
    this.doc.patchCanvas(patch);
  }

  setAutoRefresh(value: unknown): void {
    const seconds = autoRefreshSecondsOf(value);
    this.setCanvas({ autoRefreshSeconds: seconds || undefined });
  }

  /** Canvas W / H, applied once on Enter / blur; the layout adjusts to the new width. */
  setCanvasSize(side: 'width' | 'height', raw: unknown): void {
    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) return;
    this.setCanvas({ [side]: Math.max(side === 'width' ? 320 : 200, Math.round(value)) });
  }

  setGrid(patch: { size?: number; snap?: boolean; visible?: boolean }): void {
    this.doc.setGrid(patch);
  }

  /** Number inputs hand back strings; this keeps the templates free of casts. */
  /** Geometry is stored fractional (fluid screens scale it); show it to one decimal. */
  px(value: number): number {
    return roundGeometry(value);
  }

  num(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
}

/** The widget type a Table type pick switches to, or null when it stays the same widget. */
function tableRetypeFor(layoutType: string, variant: unknown): string | null {
  if (typeof variant !== 'string' || !variant) return null;
  if (layoutType === 'TABLE_MATRIX' && variant === 'nested') return 'NESTED_TABLE';
  if (layoutType === 'NESTED_TABLE' && variant !== 'nested') return 'TABLE_MATRIX';
  return null;
}
